import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import Title from '@zenbusiness/common-components/Title';
import classNames from 'classnames';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';

import SharedAsset from '#client/components/SharedAsset';
import Skeleton from '#client/components/Skeleton';
import { isReserved } from '#client/utils/businessEntity';
import empty from '#client/utils/empty';
import { buildCartUrl } from '#client/utils/urls';

import AmendmentBanner from '../Amendments/AmendmentBanner';
import {
  formatAddress,
  formatVBAAddress
} from '../Orders/helpers/formatData';
import {
  formatContact,
  formatDirector,
  getStateName
} from './helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 16,
    [theme.breakpoints.up('md')]: {
      padding: 40
    }
  },
  profileHead: {
    marginBottom: 26,
    [theme.breakpoints.up('md')]: {
      marginBottom: 48
    }
  },
  amendment: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row'
    }
  },
  amendmentIcon: {
    marginBottom: '24px',
    alignSelf: 'center',
    width: '48px',
    [theme.breakpoints.up('md')]: {
      marginBottom: '0',
      marginRight: '24px',
      width: '80px'
    }
  },
  amendmentBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    '& > p': {
      textAlign: 'center'
    },
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-start'
    }
  },
  amendmentCTA: {
    marginTop: 24,
    [theme.breakpoints.up('md')]: {
      marginTop: '0'
    }
  },
  section: {
    '& > p': {
      fontWeight: 'bold',
      margin: '0 0 4px 0'
    },
    marginBottom: 32,

    [theme.breakpoints.up('md')]: {
      marginBottom: 60
    }
  },
  sectionHead: {
    ...theme.typography.title_smallRegular
  },
  sectionContent: {
    '& pre': {
      overflow: 'hidden',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      marginTop: 0,
      marginBottom: '8px',
      fontFamily: theme.typography.fontFamily,
      [theme.breakpoints.up('sm')]: {
        ...theme.typography.body_extraLargeRegular
      }
    },

    [theme.breakpoints.up('md')]: {
      ...theme.typography.title_smallRegular
    }
  },
  itemTitle: {
    ...theme.typography.caption_mediumRegular,
    color: '#6D7072',
    marginBottom: 5,
    marginTop: 9,

    [theme.breakpoints.up('md')]: {
      ...theme.typography.body_smallRegular
    }
  },
  itemTitleSubLabel: {
    display: 'inline-block',
    verticalAlign: 'middle',
    color: theme.palette.text.light,
    ...theme.typography.title_smallBold
  },
  card: {
    padding: '32px',
    marginBottom: 32
  },
  tip: {
    ...theme.typography.body_largeRegular,
    padding: '16px',
    background: theme.palette.primary.main,
    borderRadius: '16px',
    [theme.breakpoints.up('md')]: {
      maxWidth: 400
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 500
    }
  },
  dangerTip: {
    backgroundColor: '#E53535'
  },
  tooltip: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: 8,
    height: '15px',
    width: '15px',

    '& img': {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '100%'
    }
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: theme.palette.primary.contrastText,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      '& img': {
        minWidth: 64,
        marginRight: 16
      }
    }
  },
  arrow: {
    color: theme.palette.primary.main
  },
  dangerArrow: {
    color: '#E53535'
  }
}));

export const businessTypes = {
  llc: 'Limited Liability Company',
  corp: 'Corporation',
  sole_prop: 'Sole Proprietorship'
};

const BusinessProfile = ({
  loading,
  businessEntity = {},
  virtualAddress = {},
  entitlements = {},
  pricing = [],
  registeredAgents = []
}) => {
  const classes = useStyles();
  const { businessEntityUuid } = useBusinessEntity();
  const {
    businessEntityName: name,
    businessEntityType: type,
    businessEntityType,
    businessIndustry: industry,
    physicalAddress,
    mailingAddress,
    principalAddress,
    businessEntityData: {
      authorizedSignatory: signatory,
      businessPurpose: purpose,
      directors = [],
      domesticJurisdiction,
      ein,
      managerType,
      managers = [],
      members = [],
      officers = [],
      officialContact,
      sopContact: sop
    } = {}
  } = businessEntity;

  const hasVba = entitlements.has('virtual_business_address');
  const formationEntitlement = entitlements.get(`${type}_formation`);
  const formationFulfillment = formationEntitlement?.fulfillment;
  const isFormationCompleted = formationFulfillment?.derivedStatus === 'active';
  const isReservedEntity = isReserved(businessEntity, entitlements);
  const amendmentPricingObj = pricing.find(({ service }) => service === `${type}_amendment`);
  const amendmentPricing = amendmentPricingObj?.amount;
  const managerTypeDesc = {
    AllOwners: 'All the owners (Member managed)',
    SomeOwners: 'Only some of the owners (Manager managed)',
    SomeManagers: 'Some or all of the owners and other managers (Manager managed)',
    AllManagers: 'None of the owners, only other managers (Manager-managed)'
  };
  const registrations = registeredAgents
    // eslint-disable-next-line no-nested-ternary
    .sort((a, b) => (a.isDomestic ? -1 : b.isDomestic ? 1 : 0))
    .map(({
      jurisdiction,
      isDomestic,
      electiveCEO,
      filingID,
      filingDate,
      statePin
    }) => {
      const items = [{
        label: 'Registration Type',
        content: isDomestic ? 'Domestic' : 'Foreign',
        tooltip: isDomestic
          ? 'The state where you first formed your business is called your home or domestic state.'
          : `Any state you registered or qualified in after ${getStateName(domesticJurisdiction)} is considered a
          foreign state.`,
        xs: 12,
        sm: 6
      }];
      if (filingID) {
        items.push({
          label: 'Business ID',
          content: filingID,
          xs: 12,
          sm: 6
        });
      }
      if (filingDate) {
        items.push({
          label: 'Registration Date',
          content: format(parseISO(filingDate), 'MM/dd/yyyy'),
          xs: 12,
          sm: 6
        });
      }
      if (jurisdiction === 'CA' && electiveCEO) {
        items.push({
          label: 'Elective CEO',
          content: formatContact(electiveCEO),
          xs: 12,
          sm: 6
        });
      }
      if (jurisdiction === 'VA' && statePin) {
        items.push({
          label: 'State PIN',
          content: statePin,
          xs: 12,
          sm: 6
        });
      }
      return { title: `${getStateName(jurisdiction)} Registration`, widths: { xs: 12 }, items };
    });

  const sections = [
    {
      title: `${isReservedEntity ? 'Reserved ' : ''}Business Name`,
      widths: { xs: 12, sm: 6 },
      filter: !!name,
      items: [
        { content: name, xs: 12 }
      ]
    },
    {
      title: `${isReservedEntity ? 'Reserved ' : ''}Business Type`,
      widths: { xs: 12, sm: 6 },
      items: [
        { content: businessTypes[type], xs: 12 }
      ]
    },
    {
      title: 'Employer ID Number (EIN)',
      widths: { xs: 12, sm: 6 },
      filter: !!ein,
      items: [
        { content: ein, xs: 12 }
      ]
    },
    {
      title: 'Business Purpose',
      widths: { xs: 12, sm: 6 },
      filter: !!purpose,
      items: [
        { content: purpose, xs: 12 }
      ]
    },
    {
      title: 'Business Industry',
      widths: { xs: 12, sm: 6 },
      filter: !!industry,
      items: [
        { content: industry, xs: 12 }
      ]
    },
    ...registrations,
    {
      title: 'Structure (Who will run your business)',
      widths: { xs: 12 },
      filter: !!managerType,
      items: [
        { content: managerTypeDesc[managerType] || managerType, xs: 12 }
      ]
    },
    {
      title: 'Directors',
      widths: { xs: 12, sm: 6 },
      filter: directors !== null && directors.length !== 0,
      items: directors && directors.map((manager) => ({
        content: formatDirector(manager),
        xs: 12
      }))
    },
    {
      title: 'Officers',
      widths: { xs: 12, sm: 6 },
      filter: officers !== null && officers.length !== 0,
      items: officers && officers.map((officer) => ({
        content: formatDirector(officer),
        xs: 12
      }))
    },
    {
      title: 'Managers',
      widths: { xs: 12, sm: 6 },
      filter: managers !== null && managers.length !== 0,
      items: managers && managers.map((manager) => ({
        content: formatContact(manager),
        xs: 12
      }))
    },
    {
      title: 'Members',
      widths: { xs: 12, sm: 6 },
      filter: members !== null && members.length !== 0,
      items: members && members.map((manager) => ({
        content: formatContact(manager),
        xs: 12
      }))
    },
    {
      title: 'Addresses',
      widths: { xs: 12 },
      items: [
        {
          tooltip: `Some states require a physical address.  The physical business address is the
          address where business will be conducted.  It can be your home address or an office, but
          it cannot be a P.O. Box.`,
          label: 'Physical Address',
          content: physicalAddress ? formatAddress(physicalAddress) : 'None',
          xs: 12,
          sm: 6
        },
        {
          tooltip: `A business mailing address is where you will receive mail for your business.
          It can be an official business address, P.O. Box, or personal address.  If you are using
          ZenBusiness as your registered agent, you cannot use our street address as your business
          mailing address.`,
          label: 'Mailing Address',
          content: mailingAddress ? formatAddress(mailingAddress) : 'None',
          xs: 12,
          sm: 6
        },
        {
          tooltip: `A principal address is an office where official records are kept.  It can be
          and often is the main or home office address for the business and in some instances can
          be an out of state address.  It can NOT be a P.O. Box or the registered agent's office
          address provided by ZenBusiness.  If you are already established at the Secretary of
          State this address can be found on most state filing paperwork.`,
          label: 'Principal Address',
          content: principalAddress ? formatAddress(principalAddress) : 'None',
          xs: 12,
          sm: 6
        },
        (Object.keys(virtualAddress).length && {
          label: `Virtual Address${!hasVba ? ' (Inactive)' : ''}`,
          content: virtualAddress ? formatVBAAddress(virtualAddress) : 'None',
          xs: 12,
          md: 6,
          useToolTipDangerColor: !hasVba,
          tooltip: (
            <>
              {!hasVba && (
                <>
                  You no longer have this product activated.
                  <br />
                </>
              )}
              This virtual address is our mail center in Austin, Texas that acts as the real
              mailing address for your business. The mail center packages your mail and delivers it
              to the physical address you have listed on your account.
            </>)
        })
      ]
    },
    {
      title: 'Registered Agent',
      widths: { xs: 12, sm: 6 },
      filter: registeredAgents !== null && registeredAgents.length !== 0,
      items: registeredAgents && registeredAgents.map(({
        jurisdiction,
        agentInformation
      }) => {
        // We remove the email because otherwise it will be rendered by
        // formatContact function and we don't want that for Registered Agent UI
        const {
          email,
          ...clearedAgent
        } = agentInformation || {};

        return ({
          label: `${getStateName(jurisdiction)} Agent`,
          content: formatContact(empty(clearedAgent) ? null : clearedAgent),
          xs: 12
        });
      })
    },
    {
      title: 'Authorized Signatory',
      widths: { xs: 12, sm: 6 },
      titleTooltip: `The "Authorized Signatory" has authority to sign documents, agreements, and
      contracts on behalf of your business.`,
      filter: !!signatory,
      items: [
        { content: signatory && formatDirector(signatory), xs: 12 }
      ]
    },
    ...(!isReservedEntity ? [{
      title: 'Official Contact',
      titleTooltip: `This "Official State Communication Contact" is mailed any official state
      documents we receive from the state.`,
      widths: { xs: 12, sm: 6 },
      filter: !!officialContact,
      items: [
        { content: officialContact && formatContact(officialContact), xs: 12 }
      ]
    }] : []),
    {
      title: 'Service of Process Contact',
      titleTooltip: `This "Service of Process Contact" is mailed any legal or service of proccess
      documents we receive from the state.`,
      widths: { xs: 12, sm: 6 },
      filter: !!sop && sop.address1 !== null,
      items: [
        { content: sop && formatContact(sop), xs: 12 }
      ]
    }
  ];

  return (
    <div className={classes.root}>
      <Title level={1} variant="lg-alt" className={classes.profileHead}>{`${isReservedEntity ? 'Reserved ' : ''}Business Profile`}</Title>
      {isFormationCompleted && (
        <AmendmentBanner
          amendmentUrl={buildCartUrl(`${businessEntityType}_amendment`, { businessEntityUuid })}
          amendmentPricing={amendmentPricing}
        />
      )}
      <Grid container>
        {sections
          .filter(({ filter }) => (loading || filter !== false))
          .map(({
            title, titleTooltip, items, widths
          }, i) => (
            // eslint-disable-next-line react/jsx-props-no-spreading, react/no-array-index-key
            <Grid item className={classes.section} key={i} {...widths}>
              {loading && <Skeleton />}
              {!loading && (
                <Title variant="sm-bold" className={classes.itemTitleSubLabel} level={3}>
                  {title}
                  {titleTooltip && (
                    <Tooltip
                      arrow
                      classes={{ tooltip: classes.tip, arrow: classes.arrow }}
                      placement="top"
                      title={(
                        <div className={classes.tooltipContent}>
                          {titleTooltip}
                        </div>
                      )}
                      className={classes.tooltip}
                      enterTouchDelay={0}
                      leaveTouchDelay={15000}
                    >
                      {/*
                        Tooltip have issues working with functional components,
                        wrapping up the component with an html element fix it
                      */}
                      <span>
                        <SharedAsset src="/icon/micro/info-circle.svg" width={24} alt="tip" className={classes.largeTipIcon} />
                      </span>
                    </Tooltip>
                  )}
                </Title>

              )}

              <Grid
                container
                direction="row"
                alignItems="flex-start"
                className={classes.sectionContent}
              >
                {items.map(({
                  content, label, tooltip, useToolTipDangerColor, ...props
                }, j) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading, react/no-array-index-key
                  <Grid item {...props} className={classes.sectionItem} key={j}>
                    {loading && <Skeleton />}

                    {(!loading && label) && (
                      <div className={classes.itemTitle}>
                        <Title variant="sm-bold" className={classes.itemTitleSubLabel} level={4}>
                          {label}
                        </Title>
                        {tooltip && (
                          <Tooltip
                            arrow
                            classes={{
                              tooltip: classNames(
                                classes.tip,
                                useToolTipDangerColor && classes.dangerTip
                              ),
                              arrow: useToolTipDangerColor
                                ? classes.dangerArrow
                                : classes.arrow
                            }}
                            placement="top"
                            title={(
                              <div className={classes.tooltipContent}>
                                {tooltip}
                              </div>
                            )}
                            className={classes.tooltip}
                            enterTouchDelay={0}
                            leaveTouchDelay={15000}
                          >
                            <span>
                              <SharedAsset src="/icon/micro/info-circle.svg" width={16} alt="tip" />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    )}

                    {content}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ))}
      </Grid>
    </div>
  );
};

BusinessProfile.propTypes = {
  virtualAddress: PropTypes.shape({}),
  loading: PropTypes.bool,
  businessEntity: PropTypes.shape({}).isRequired,
  entitlements: PropTypes.shape({}).isRequired,
  pricing: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  registeredAgents: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

BusinessProfile.defaultProps = {
  virtualAddress: {}
};

export default BusinessProfile;
